export type LobbyHighFiveContact = readonly [leftSessionId: string, rightSessionId: string];

export type LobbyHighFiveEvent = {
    leftSessionId: string;
    rightSessionId: string;
};

const getPairKey = (leftSessionId: string, rightSessionId: string): string =>
    leftSessionId < rightSessionId
        ? `${leftSessionId}\u0000${rightSessionId}`
        : `${rightSessionId}\u0000${leftSessionId}`;

/**
 * Keeps the lobby high-five deliberately disposable: each pair cools down independently,
 * and a pair must physically separate before another contact can count.
 */
export class LobbyHighFiveTracker {
    private readonly cooldownMs: number;
    private touchingPairs = new Set<string>();
    private readonly lastHighFiveAtByPair = new Map<string, number>();

    constructor(cooldownMs: number) {
        this.cooldownMs = Math.max(0, cooldownMs);
    }

    update(contacts: LobbyHighFiveContact[], recordedAt: number): LobbyHighFiveEvent[] {
        for (const [pair, lastAt] of this.lastHighFiveAtByPair) {
            if (recordedAt - lastAt >= this.cooldownMs) this.lastHighFiveAtByPair.delete(pair);
        }
        const nextTouchingPairs = new Set<string>();
        const events: LobbyHighFiveEvent[] = [];

        for (const [leftSessionId, rightSessionId] of contacts) {
            if (!leftSessionId || !rightSessionId || leftSessionId === rightSessionId) {
                continue;
            }

            const pairKey = getPairKey(leftSessionId, rightSessionId);

            if (nextTouchingPairs.has(pairKey)) {
                continue;
            }

            nextTouchingPairs.add(pairKey);

            if (this.touchingPairs.has(pairKey)) {
                continue;
            }

            const readyAt =
                (this.lastHighFiveAtByPair.get(pairKey) ?? Number.NEGATIVE_INFINITY) + this.cooldownMs;
            if (recordedAt < readyAt) {
                continue;
            }
            this.lastHighFiveAtByPair.set(pairKey, recordedAt);
            events.push({ leftSessionId, rightSessionId });
        }

        this.touchingPairs = nextTouchingPairs;
        return events;
    }

    reset() {
        this.touchingPairs.clear();
        this.lastHighFiveAtByPair.clear();
    }
}
